import styled from 'styled-components'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  getFactoryModelsState,
  getMarketLanguageState,
  getMarketSettingsState,
  getVersionInfoState,
} from '../../store/appSlice'
import { getGoEtelLastUpdatedState } from '../../store/sessionDataSlice'
import { TdsAccordion, TdsAccordionItem, TdsButton } from '@scania/tegel-react'
import { TextId, useTextsV2, TextGetterV2 } from '../../utils/useTexts'
import { openModalUploadXml } from '../../store/modalSlice'
import { TestElementTypeId } from '../../types/TestAttributeId'
import convertNewlineToJSX from '../../utils/convertNewlineToJSX'

const InfoRoot = styled.div``

const TermsAndConditionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const AccordionContentRoot = styled.div``

const AboutWrapper = styled.div`
  display: flex;
  width: 100%;
`

const AboutRightContainer = styled.div`
  width: 40%;
  right: 0px;
  float: right;
`

const AboutLeftContainer = styled.div`
  width: 60%;
`

const SmallHeader = styled.h5`
  font-size: 11px;
`

const AboutSection = styled.div`
  margin-top: 0;
  margin-bottom: 8px;
`

const Text = styled.div`
  font-size: 11px;
  margin: 0px;
  line-height: 16px;
`

const Link = styled.div`
  font-size: 11px;
  margin: 0px;
  margin-top: 6px;
  margin-left: 8px;
  line-height: 13px;
`
interface SpecialLinkEntry {
  text: string
  link: string
}

interface GuiInfo {
  version: string
  buildNumber: number
}

interface ContentInfoProps {
  aboutVisible: boolean
}

export function ContentInfo(props: ContentInfoProps): JSX.Element {
  const dispatch = useAppDispatch()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const t2 = useTextsV2()
  const vi = useAppSelector(getVersionInfoState)
  const ms = useAppSelector(getMarketSettingsState)
  const goEtelLastUpdated = useAppSelector(getGoEtelLastUpdatedState)
  const factoryModelsState = useAppSelector(getFactoryModelsState)
  const [guiInfo, setGuiInfo] = useState<GuiInfo | null>(null)

  const lastUpdatedGo = goEtelLastUpdated?.toISOString()
  const lastUpdatedFM =
    factoryModelsState.factoryModels?.response.secmLastUpdated

  useEffect(() => {
    const asyncWrapper = async () => {
      try {
        const response = await fetch('version.json')
        const obj = await response.json()
        const branchAny = obj?.git?.branch
        const branch: string = typeof branchAny === 'string' ? branchAny : ''
        const regex = /^.*_(.*)/
        const match = regex.exec(branch)
        const version = match?.at(1) || ''
        const buildNumber = obj?.jenkins?.buildNumber || null
        const info: GuiInfo = { buildNumber, version }
        setGuiInfo(info)
      } catch {
        console.warn('Failed to get frontend version.')
        return
      }
    }
    asyncWrapper()
  }, [])

  const handleXmlUploadClick = () => {
    if (!marketLanguage) {
      console.error('Expected marketLanguage to be defined.')
      return
    }
    dispatch(openModalUploadXml())
  }

  function getSpecialLinkEntries(
    etelMarketCode: number | null,
  ): Array<SpecialLinkEntry> {
    let specialLinks: Array<SpecialLinkEntry> = []
    switch (etelMarketCode) {
      // Germany
      case 5145:
        specialLinks = [
          {
            text: 'Impressum',
            link: 'https://www.scania.com/de/de/home/admin/misc/impressum.html',
          },
          {
            text: 'Datenschutz',
            link: 'https://www.scania.com/de/de/home/admin/misc/privacy-statement.html',
          },
        ]
        break
      // Austria
      case 5144:
        specialLinks = [
          {
            text: 'Impressum',
            link: 'https://www.scania.com/at/de/home/admin/misc/impressum.html',
          },
          {
            text: 'Datenschutz',
            link: 'https://www.scania.com/at/de/home/admin/misc/privacy-statement.html',
          },
        ]
        break
      default:
        break
    }
    return specialLinks
  }

  const buildSpecialMarketLegalLinkElements = (
    etelMarketCode: number | null,
  ) => {
    const specialLinks = getSpecialLinkEntries(etelMarketCode)
    return (
      <>
        {specialLinks.map((value, i) => {
          return (
            <Link key={'special_legal_link_' + i}>
              <a href={value.link} target="_blank" rel="noreferrer">
                {value.text}
              </a>
            </Link>
          )
        })}
      </>
    )
  }

  function TEST(t: TextGetterV2) {
    return (
      <Link>
        <a
          href={t2(TextId.LINK_LEGAL)}
          target="_blank"
          rel="noreferrer"
          data-test-element-type={TestElementTypeId.LinkLegal}
        >
          {t2(TextId.SCANIA_COPYRIGHT)}
        </a>
      </Link>
    )
  }

  return (
    <InfoRoot className="tds-on-white-bg">
      <TdsAccordion
        style={{ margin: '16px' }}
        data-test-element-type={TestElementTypeId.AccordionTermsAndCondition}
      >
        <TdsAccordionItem
          header={t2(TextId.LABEL_TERMS_AND_CONDITIONS)}
          expand-icon-position="end"
        >
          <TermsAndConditionWrapper>
            <Text
              data-test-element-type={TestElementTypeId.TextTermsAndCondition}
            >
              {convertNewlineToJSX(t2(TextId.TEXT_TERMS_AND_CONDITIONS))}
            </Text>
            {buildSpecialMarketLegalLinkElements(
              marketLanguage?.market || null,
            )}
            <Link>
              <a
                href={t2(TextId.LINK_DISCLAIMER)}
                target="_blank"
                rel="noreferrer"
                data-test-element-type={TestElementTypeId.LinkDisclaimer}
              >
                {t2(TextId.LINK_TEXT_DISCLAIMER)}
              </a>
            </Link>
            {TEST(t2)}
          </TermsAndConditionWrapper>
        </TdsAccordionItem>
      </TdsAccordion>

      <TdsAccordion
        style={{ margin: '16px' }}
        data-test-element-type={TestElementTypeId.AccordionUploadConfig}
      >
        <TdsAccordionItem
          header={t2(TextId.ACTION_UPLOAD_CONFIG)}
          expand-icon-position="end"
        >
          <TermsAndConditionWrapper>
            <Text
              data-test-element-type={TestElementTypeId.DescriptionXmlUpload}
            >
              {t2(TextId.DESCRIPTION_XML_UPLOAD)}
            </Text>
            <TdsButton
              type="button"
              variant="primary"
              size="md"
              text={t2(TextId.ACTION_UPLOAD)}
              onClick={handleXmlUploadClick}
              data-test-element-type={TestElementTypeId.UploadConfigButton}
              style={{ marginTop: '12px' }}
              fullbleed={true}
            />
          </TermsAndConditionWrapper>
        </TdsAccordionItem>
      </TdsAccordion>

      {props.aboutVisible && (
        <TdsAccordion
          style={{ margin: '16px' }}
          data-test-element-type={TestElementTypeId.AccordionAbout}
        >
          <TdsAccordionItem
            header={t2(TextId.LABEL_ABOUT)}
            expand-icon-position="end"
          >
            <AccordionContentRoot
              data-test-element-type={TestElementTypeId.AboutContent}
            >
              <AboutSection>
                <SmallHeader>Etel</SmallHeader>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Market name:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{ms?.marketFullName}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>MarketId:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{marketLanguage?.market}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>LanguageId:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{marketLanguage?.language}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
                {ms?.guidedOfferingSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>View Id, GO Step 1:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>
                          {ms.guidedOfferingSettings.etelViewIdGoStep1}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>View Id, GO Step 2:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>
                          {ms.guidedOfferingSettings.etelViewIdGoStep2}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
                {ms?.factoryModelsSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>View Id, FM:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>{ms.factoryModelsSettings.etelViewId}</Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
                {ms?.guidedOfferingSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>Part Period, GO Step 1:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>
                          {ms.guidedOfferingSettings.partPeriodGoStep1}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>Part Period, GO Step 2:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>
                          {ms.guidedOfferingSettings.partPeriodGoStep2}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
                {ms?.factoryModelsSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>Part Period, FM:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text>{ms.factoryModelsSettings.partPeriod}</Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
                {ms?.guidedOfferingSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>SECM Last Updated, GO:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text title={lastUpdatedGo}>
                          {lastUpdatedGo?.split('T')[0]}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
                {ms?.factoryModelsSettings && (
                  <>
                    <AboutWrapper>
                      <AboutLeftContainer>
                        <Text>SECM Last Updated, FM:</Text>
                      </AboutLeftContainer>
                      <AboutRightContainer>
                        <Text title={lastUpdatedFM || ''}>
                          {lastUpdatedFM?.split('_')[0]}
                        </Text>
                      </AboutRightContainer>
                    </AboutWrapper>
                  </>
                )}
              </AboutSection>

              <AboutSection>
                <SmallHeader>Content versions</SmallHeader>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Images:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text
                      data-test-element-type={
                        TestElementTypeId.AboutContentImagesVersion
                      }
                    >
                      {vi?.buildInfo.contentVersions[0].version}
                    </Text>
                  </AboutRightContainer>
                </AboutWrapper>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Resources:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text
                      data-test-element-type={
                        TestElementTypeId.AboutContentResourcesVersion
                      }
                    >
                      {vi?.buildInfo.contentVersions[1].version}
                    </Text>
                  </AboutRightContainer>
                </AboutWrapper>
              </AboutSection>

              <AboutSection>
                <SmallHeader>Backend</SmallHeader>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Version name:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text
                      data-test-element-type={
                        TestElementTypeId.AboutBackendVersion
                      }
                    >
                      {vi?.buildInfo.applicationVersion?.version ||
                        'dev. build'}
                    </Text>
                  </AboutRightContainer>
                </AboutWrapper>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Build number:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{vi?.buildInfo.buildNumber}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
              </AboutSection>

              <AboutSection>
                <SmallHeader>Frontend</SmallHeader>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Version name:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{guiInfo?.version || ''}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
                <AboutWrapper>
                  <AboutLeftContainer>
                    <Text>Build number:</Text>
                  </AboutLeftContainer>
                  <AboutRightContainer>
                    <Text>{guiInfo?.buildNumber || -1}</Text>
                  </AboutRightContainer>
                </AboutWrapper>
              </AboutSection>
            </AccordionContentRoot>
          </TdsAccordionItem>
        </TdsAccordion>
      )}
    </InfoRoot>
  )
}
